import { Icons } from '@/assets';
import React, { useState, useRef, useEffect } from 'react';
import { ReactSVG } from 'react-svg';

const TimePickerV2 = ({
  value,
  onChange,
  placeholder = 'Select time',
  disabled = false,
  error = null,
  inputHeightClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [inputError, setInputError] = useState(null);

  const popupRef = useRef(null);
  const inputRef = useRef(null);

  const isIPadOrIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Parse existing value when component mounts or value changes
  useEffect(() => {
    if (value !== null && value !== undefined) {
      // If value is a number (24-hour format)
      if (typeof value === 'number') {
        setSelectedTime({
          hour: value,
          minute: 0,
        });
        setInputValue(formatTime({ hour: value, minute: 0 }));
      } else if (typeof value === 'string') {
        // Handle time span format (HH:MM) - 24-hour format
        if (value.includes(':') && !value.includes(' ')) {
          // Time span format like "06:45" or "24:00"
          const [hour, minute] = value.split(':');
          const hour24 = parseInt(hour);
          const minuteValue = parseInt(minute) || 0;

          setSelectedTime({
            hour: hour24,
            minute: minuteValue,
          });
          setInputValue(value);
        } else {
          // Handle existing string format with AM/PM - convert to 24-hour
          const [time, period] = value.split(' ');
          if (time && period) {
            const [hour, minute] = time.split(':');
            let hour24 = parseInt(hour);

            if (period === 'PM' && hour24 !== 12) {
              hour24 = hour24 + 12;
            } else if (period === 'AM' && hour24 === 12) {
              hour24 = 0;
            }

            const timeObj = {
              hour: hour24,
              minute: parseInt(minute) || 0,
            };
            setSelectedTime(timeObj);
            setInputValue(formatTime(timeObj));
          }
        }
      }
    } else {
      setInputValue('');
    }
  }, [value]);

  // Handle outside click to close popup
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  // Handle scroll and resize events to update popup position
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        // Close popup when user starts scrolling
        setIsOpen(false);
      }
    };

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleInputClick = () => {
    if (!disabled) {
      if (!isOpen) {
        // Set default time when opening popup if no time is selected
        if (!selectedTime) {
          const defaultTime = {
            hour: 0,
            minute: 0,
          };
          setSelectedTime(defaultTime);
          // Also call onChange to notify parent component
          onChange('00:00');
        }

        setIsOpen(true);
      } else {
        // Close if already open
        setIsOpen(false);
      }
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const hour = time.hour.toString().padStart(2, '0');
    const minute = time.minute.toString().padStart(2, '0');

    // Always show full time format with minutes in 24-hour format (e.g., "12:00")
    return `${hour}:${minute}`;
  };

  // Validate time input format (HH:MM)
  const validateTimeInput = (input) => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    return timeRegex.test(input);
  };

  // Handle manual input changes
  const handleInputChange = (e) => {
    const value = e.target.value;

    // Only allow digits, colon, and empty string
    if (value !== '' && !/^[\d:]*$/.test(value)) {
      return; // Don't update if invalid characters are entered
    }

    // Prevent more than one colon
    if ((value.match(/:/g) || []).length > 1) {
      return;
    }

    // Prevent colon at the beginning
    if (value.startsWith(':')) {
      return;
    }

    // Limit total length to 5 characters (HH:MM)
    if (value.length > 5) {
      return;
    }

    setInputValue(value);
    setInputError(null);
    setIsEditing(true);

    // If input is empty, clear the selected time
    if (value === '') {
      setSelectedTime(null);
      onChange('');
      return;
    }

    // Validate format
    if (validateTimeInput(value)) {
      const [hour, minute] = value.split(':');
      const newTime = {
        hour: parseInt(hour),
        minute: parseInt(minute),
      };
      setSelectedTime(newTime);
      onChange(value);
    }
  };

  // Handle input focus
  const handleInputFocus = (e) => {
    // On iOS, prevent keyboard from opening by blurring immediately
    // The click handler will open the popper
    if (isIPadOrIOS) {
      e.target.blur();
      return;
    }
    setIsEditing(true);
    setInputError(null);
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Close popup on Enter
      if (isOpen) {
        setIsOpen(false);
      }
      // Blur input to trigger validation
      e.target.blur();
    } else if (e.key === 'Escape') {
      // Close popup on Escape
      if (isOpen) {
        setIsOpen(false);
      }
      // Revert input to last valid value
      if (selectedTime) {
        setInputValue(formatTime(selectedTime));
      } else {
        setInputValue('');
      }
      setInputError(null);
      e.target.blur();
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    setIsEditing(false);

    // If input is invalid on blur, revert to last valid value
    if (inputValue && !validateTimeInput(inputValue)) {
      if (selectedTime) {
        setInputValue(formatTime(selectedTime));
      } else {
        setInputValue('');
      }
      setInputError(null);
    }
  };

  const updateTime = (newTime) => {
    setSelectedTime(newTime);

    // Format as HH:MM time span in 24-hour format
    const formattedTime = `${newTime.hour
      .toString()
      .padStart(2, '0')}:${newTime.minute.toString().padStart(2, '0')}`;

    // Update input value when time is changed via popup
    setInputValue(formattedTime);
    onChange(formattedTime);
  };

  const incrementHour = () => {
    if (!selectedTime) {
      updateTime({ hour: 0, minute: 0 });
      return;
    }
    const newHour = selectedTime.hour === 23 ? 0 : selectedTime.hour + 1;
    updateTime({ ...selectedTime, hour: newHour });
  };

  const decrementHour = () => {
    if (!selectedTime) {
      updateTime({ hour: 23, minute: 0 });
      return;
    }
    const newHour = selectedTime.hour === 0 ? 23 : selectedTime.hour - 1;
    updateTime({ ...selectedTime, hour: newHour });
  };

  const incrementMinute = () => {
    if (!selectedTime) {
      updateTime({ hour: 0, minute: 1 });
      return;
    }
    const newMinute = selectedTime.minute === 59 ? 0 : selectedTime.minute + 1;
    updateTime({ ...selectedTime, minute: newMinute });
  };

  const decrementMinute = () => {
    if (!selectedTime) {
      updateTime({ hour: 0, minute: 59 });
      return;
    }
    const newMinute = selectedTime.minute === 0 ? 59 : selectedTime.minute - 1;
    updateTime({ ...selectedTime, minute: newMinute });
  };

  const displayValue = isEditing
    ? inputValue
    : value !== null && value !== undefined
    ? selectedTime
      ? formatTime(selectedTime)
      : value
    : placeholder;

  return (
    <div className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onClick={handleInputClick}
          disabled={disabled}
          readOnly={isIPadOrIOS}
          placeholder={placeholder}
          className={`
            w-full px-2 h-7 pr-6 border hover:shadow-button-hover hover:border-black text-11 box-border border-solid rounded-md transition-colors duration-200 ${inputHeightClassName}
            ${error || inputError ? 'border-red-500' : 'border-gray-300'}
            ${!value && !isEditing && 'text-light-gray-border'}
            ${
              disabled
                ? 'bg-border-light-secondary cursor-not-allowed text-light-gray-border'
                : 'bg-white hover:border-primary-text focus:border-primary-text cursor-text'
            }
            focus:outline-none
          `}
        />
        <div className="absolute right-2 flex top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            {isOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            )}
          </svg>
        </div>
      </div>
      {isOpen && (
        <div
          id="time-picker-v2-container"
          ref={popupRef}
          className="absolute top-full mt-2 w-full box-border bg-white border-solid border border-border-primary rounded shadow-md py-1 px-2"
          style={{
            zIndex: 99999999,
          }}>
          <div className="flex items-center justify-between">
            {/* Hour Section */}
            <div className="flex flex-col items-center">
              <div className="flex flex-col   items-center rounded-sm">
                <button
                  type="button"
                  onClick={incrementHour}
                  className="bg-transparent cursor-pointer shadow-none p-0 border-none hover:bg-gray-100 text-gray-600 text-sm">
                  <ReactSVG src={Icons.arrowUpSmall} className="" />
                </button>
                <span className="px-2  text-11 font-medium  text-center text-primary-text">
                  {selectedTime
                    ? selectedTime.hour.toString().padStart(2, '0')
                    : '--'}
                </span>
                <button
                  type="button"
                  onClick={decrementHour}
                  className="bg-transparent cursor-pointer shadow-none p-0 border-none hover:bg-gray-100 text-gray-600 text-sm">
                  <ReactSVG
                    src={Icons.arrowUpSmall}
                    className="transform rotate-180"
                  />
                </button>
              </div>
            </div>

            {/* Separator */}
            <div className="text-xl font-bold text-gray-400">:</div>

            {/* Minute Section */}
            <div className="flex flex-col items-center">
              <div className="flex  flex-col items-center  rounded-sm">
                <button
                  type="button"
                  onClick={incrementMinute}
                  className="bg-transparent cursor-pointer shadow-none p-0 border-none hover:bg-gray-100 text-gray-600 text-sm">
                  <ReactSVG src={Icons.arrowUpSmall} className="" />
                </button>
                <span className="px-2  text-11 font-medium  text-center text-primary-text">
                  {selectedTime
                    ? selectedTime.minute.toString().padStart(2, '0')
                    : '--'}
                </span>
                <button
                  type="button"
                  onClick={decrementMinute}
                  className="bg-transparent cursor-pointer shadow-none p-0 border-none hover:bg-gray-100 text-gray-600 text-sm">
                  <ReactSVG
                    src={Icons.arrowUpSmall}
                    className="transform rotate-180"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {(error || inputError) && (
        <span className="text-error text-11 mt-1 block">
          {error || inputError}
        </span>
      )}
    </div>
  );
};

export default TimePickerV2;
